import type {
  PlansResponse,
  CreateOrderResponse,
  AgenticCartBundleSummaryResponse,
  CreateAgenticCartOrderResponse,
  VerifyAgenticCartPaymentResponse,
  CreateSubscriptionResponse,
  VerifySubscriptionResponse,
  RateReadingRequest,
} from "@ishvaram/api-client";
import { identifyUser } from "@/lib/analytics-user";
import { assertResolutionComponentPayload } from "@/lib/payment-resolution-contract";
import { getTurnstileToken } from "@/lib/turnstile";
import { apiRequest, authJsonHeaders } from "./_fetch";
import { paymentLandingAttribution } from "./payment-landing-attribution";
import { paymentDistinctId, paymentPageContext } from "./payment-context";
import type {
  AgenticCartCreateInput,
  CreatePaymentOrderInput,
  MembershipSubscriptionCreateInput,
} from "./plans-types";

export type { PlansResponse, CreateOrderResponse, CreateSubscriptionResponse };
export type { CreditPack, PremiumReport, Subscription, Session } from "@ishvaram/api-client";
export type { AgenticCartShippingAddress } from "./plans-types";
export { getReportDownloadUrl } from "./report-download-url";

export async function subscribe(data: {
  email?: string;
  phone?: string;
  moon_rashi?: string;
}): Promise<{ success: boolean; message: string }> {
  return apiRequest("/subscribe", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

export async function getPlans(language = "en"): Promise<PlansResponse> {
  const params = new URLSearchParams();
  if (language !== "en") params.set("language", language);
  const qs = params.toString();
  return apiRequest<PlansResponse>(`/plans${qs ? `?${qs}` : ""}`);
}

export async function getCheckoutUrl(data: {
  plan_id: string;
  plan_type: string;
  reading_id?: string;
  session_id?: string;
}): Promise<{ checkout_url: string; plan_id: string; plan_type: string }> {
  return apiRequest("/checkout", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

export async function createPaymentOrder(
  data: CreatePaymentOrderInput
): Promise<CreateOrderResponse> {
  assertResolutionComponentPayload(data);
  // Identity-resolution choke point: every checkout flows through here.
  // If the buyer gave us phone/email, attach an owned identity event and always
  // attach the browser distinct ID so the private intent + audit log carry it.
  const stableId = data.phone || data.email;
  if (stableId) {
    identifyUser(stableId, { email: data.email, phone: data.phone });
  }
  const checkoutAttemptId = data.idempotency_key ?? data.event_id ?? crypto.randomUUID();
  const enriched = {
    ...paymentPageContext(),
    ...data,
    ...paymentLandingAttribution(data),
    idempotency_key: checkoutAttemptId,
    event_id: data.event_id ?? checkoutAttemptId,
    posthog_distinct_id: paymentDistinctId(data.posthog_distinct_id),
    // Cloudflare Turnstile token (empty when the feature is dark / not solved).
    // Verified server-side; ignored entirely until Turnstile is enabled.
    turnstile_token: getTurnstileToken() ?? "",
  };
  return apiRequest<CreateOrderResponse>("/payment/create-order", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(enriched),
  });
}

export async function getAgenticCartBundle(
  bundleId: string
): Promise<AgenticCartBundleSummaryResponse> {
  return apiRequest<AgenticCartBundleSummaryResponse>(
    `/payment/agentic-cart-bundles/${encodeURIComponent(bundleId)}`
  );
}

export async function createAgenticCartOrder(
  data: AgenticCartCreateInput
): Promise<CreateAgenticCartOrderResponse> {
  const stableId = data.customer_phone || data.customer_email;
  if (stableId) {
    identifyUser(stableId, { email: data.customer_email, phone: data.customer_phone });
  }
  const checkoutAttemptId = data.idempotency_key ?? data.event_id ?? crypto.randomUUID();
  return apiRequest<CreateAgenticCartOrderResponse>("/payment/create-agentic-cart-order", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ...paymentPageContext(),
      ...data,
      idempotency_key: checkoutAttemptId,
      event_id: data.event_id ?? checkoutAttemptId,
      posthog_distinct_id: paymentDistinctId(data.posthog_distinct_id),
      turnstile_token: getTurnstileToken() ?? "",
    }),
  });
}

export async function verifyAgenticCartPayment(data: {
  cart_id: string;
  bundle_id: string;
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
  reading_id?: string;
  session_id?: string;
  customer_email?: string;
  customer_phone?: string;
  report_inputs?: Record<string, unknown>;
  source_path?: string;
  source?: string;
  placement?: string;
  intent?: string;
  customer_problem?: string;
  journey_action_id?: string;
  concern_id?: string;
  agent_trace_id?: string;
  selected_offer_reason?: string;
  ga_client_id?: string;
  event_id?: string;
  posthog_distinct_id?: string;
}): Promise<VerifyAgenticCartPaymentResponse> {
  return apiRequest<VerifyAgenticCartPaymentResponse>("/payment/verify-agentic-cart", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ...paymentPageContext(),
      ...data,
      posthog_distinct_id: paymentDistinctId(data.posthog_distinct_id),
    }),
  });
}

export async function createMembershipSubscription(
  data: MembershipSubscriptionCreateInput
): Promise<CreateSubscriptionResponse> {
  const headers = await authJsonHeaders();
  const checkoutAttemptId = data.idempotency_key ?? data.event_id ?? crypto.randomUUID();
  const enriched = {
    ...paymentPageContext(),
    ...data,
    ...paymentLandingAttribution(data),
    plan_type: "subscription",
    idempotency_key: checkoutAttemptId,
    event_id: data.event_id ?? checkoutAttemptId,
    posthog_distinct_id: paymentDistinctId(data.posthog_distinct_id),
    turnstile_token: getTurnstileToken() ?? "",
  };
  return apiRequest<CreateSubscriptionResponse>("/payment/create-subscription", {
    method: "POST",
    headers,
    body: JSON.stringify(enriched),
  });
}

export async function verifyPayment(data: {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
  plan_id: string;
  plan_type: string;
  reading_id?: string;
  session_id?: string;
  email?: string;
  phone?: string;
  source?: string;
  placement?: string;
  intent?: string;
  source_path?: string;
  landing_cluster?: string;
  landing_path?: string;
  customer_problem?: string;
  journey_action_id?: string;
  concern_id?: string;
  intent_taxonomy_version?: string;
  landing_intent?: string;
  chat_intent?: string;
  detected_intent?: string;
  intent_confidence?: string;
  intent_source?: string;
  paywall_variant?: string;
  question_intent?: string;
  chart_intent?: string;
  chat_signal_count?: number;
  strongest_dosha_id?: string;
  strongest_dosha_severity?: number;
  product_affinity?: string;
  is_primary_cta?: boolean;
  cta_rank?: number;
  checkout_channel?: string;
  commerce_system?: string;
  report_inputs?: Record<string, unknown>;
  /** plan_type "resolution" only (WS-K) — must match createPaymentOrder's value. */
  component_plan_id?: string;
  ga_client_id?: string;
  recovery_channel?: string;
  recovery_order_id?: string;
  /** Meta CAPI dedup: same event_id used for browser pixel + backend CAPI fire.
   *  Must match the event_id passed to createPaymentOrder. */
  event_id?: string;
  /** Legacy backend field name; value is the first-party browser analytics ID. */
  posthog_distinct_id?: string;
}): Promise<{
  success: boolean;
  credits_added: number;
  message: string;
  download_url?: string | null;
  report_job_id?: string | null;
  report_job_status?: string | null;
}> {
  assertResolutionComponentPayload(data);
  const enriched = {
    ...paymentPageContext(),
    ...data,
    posthog_distinct_id: paymentDistinctId(data.posthog_distinct_id),
  };
  return apiRequest("/payment/verify", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(enriched),
  });
}

export async function verifyMembershipSubscription(data: {
  razorpay_subscription_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
  plan_id: string;
  email?: string;
  phone?: string;
  reading_id?: string;
  session_id?: string;
  source?: string;
  placement?: string;
  intent?: string;
  source_path?: string;
  journey_action_id?: string;
  concern_id?: string;
  checkout_channel?: string;
  commerce_system?: string;
  ga_client_id?: string;
  event_id?: string;
  posthog_distinct_id?: string;
}): Promise<VerifySubscriptionResponse> {
  const headers = await authJsonHeaders();
  return apiRequest<VerifySubscriptionResponse>("/payment/verify-subscription", {
    method: "POST",
    headers,
    body: JSON.stringify({
      ...paymentPageContext(),
      ...data,
      posthog_distinct_id: paymentDistinctId(data.posthog_distinct_id),
    }),
  });
}

export type ReportJobStatus = "queued" | "running" | "completed" | "failed";

export async function getReportJobStatus(jobId: string): Promise<{
  job_id: string;
  order_id: string;
  report_type: string;
  status: ReportJobStatus;
  attempts: number;
  max_attempts: number;
  queued_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  failed_at?: string | null;
  last_error?: string | null;
  download_url?: string | null;
}> {
  return apiRequest(`/reports/jobs/${encodeURIComponent(jobId)}`);
}

export type PaymentOrderStatus = {
  order_id: string;
  status: "activated" | "pending" | "unknown" | string;
  plan_type: string;
  plan_id: string;
  credits_added: number;
  download_url?: string | null;
  delivery_status: "delivered" | "ready" | "generating" | "" | string;
  message: string;
};

export async function getPaymentOrderStatus(orderId: string): Promise<PaymentOrderStatus> {
  return apiRequest<PaymentOrderStatus>(`/payment/status/${encodeURIComponent(orderId)}`);
}

export async function abandonPayment(
  data: {
    razorpay_order_id: string;
    plan_id: string;
    plan_type: string;
    reading_id?: string;
    session_id?: string;
    email?: string;
    phone?: string;
    source?: string;
    placement?: string;
    checkout_channel?: string;
    commerce_system?: string;
    intent?: string;
    source_path?: string;
    landing_cluster?: string;
    landing_path?: string;
    customer_problem?: string;
    journey_action_id?: string;
    concern_id?: string;
    intent_taxonomy_version?: string;
    landing_intent?: string;
    chat_intent?: string;
    detected_intent?: string;
    intent_confidence?: string;
    intent_source?: string;
    paywall_variant?: string;
    question_intent?: string;
    chart_intent?: string;
    chat_signal_count?: number;
    strongest_dosha_id?: string;
    strongest_dosha_severity?: number;
    product_affinity?: string;
    is_primary_cta?: boolean;
    cta_rank?: number;
    recovery_channel?: string;
    recovery_url?: string;
    recovery_email_consent?: boolean;
    recovery_phone_consent?: boolean;
    ga_client_id?: string;
    recovery_order_id?: string;
  },
  recoveryOrder?: Pick<
    CreateOrderResponse,
    "order_id" | "plan_id" | "plan_type" | "recovery_registered"
  >
): Promise<{ success: boolean }> {
  try {
    return await apiRequest("/payment/abandon", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...paymentPageContext(), ...data }),
      // Give the optional dismissal detail a chance to survive browser unload.
      keepalive: true,
    });
  } catch (error) {
    // Recovery is already durable before checkout opens only when this exact
    // order response acknowledges it. Never infer it from consent, a plan kind,
    // a successful charge, or another order. HTTP and programming failures
    // remain actionable even when the order has a saved recovery intent.
    const recorded =
      recoveryOrder?.recovery_registered === true &&
      recoveryOrder.order_id === data.razorpay_order_id &&
      recoveryOrder.plan_id === data.plan_id &&
      recoveryOrder.plan_type === data.plan_type;
    const networkFailure =
      error instanceof TypeError &&
      /^(Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.)$/.test(
        error.message
      );
    if (recorded && networkFailure) return { success: false };
    throw error;
  }
}

export async function recordPaymentFailure(data: {
  razorpay_order_id: string;
  plan_id?: string;
  plan_type?: string;
  source?: string;
  placement?: string;
  intent?: string;
  source_path?: string;
  landing_cluster?: string;
  landing_path?: string;
  customer_problem?: string;
  journey_action_id?: string;
  concern_id?: string;
  intent_taxonomy_version?: string;
  landing_intent?: string;
  chat_intent?: string;
  detected_intent?: string;
  intent_confidence?: string;
  intent_source?: string;
  paywall_variant?: string;
  question_intent?: string;
  chart_intent?: string;
  chat_signal_count?: number;
  strongest_dosha_id?: string;
  strongest_dosha_severity?: number;
  product_affinity?: string;
  is_primary_cta?: boolean;
  cta_rank?: number;
  checkout_channel?: string;
  commerce_system?: string;
  agent_trace_id?: string;
  error_code?: string;
  error_description?: string;
  error_source?: string;
  error_step?: string;
  error_reason?: string;
  payment_id?: string;
  method?: string;
  ga_client_id?: string;
  recovery_channel?: string;
  recovery_order_id?: string;
}): Promise<{ success: boolean }> {
  return apiRequest("/payment/failure", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...paymentPageContext(), ...data }),
  });
}

/**
 * Reading-level accuracy rating — the calibration signal (rule 44). Best-effort:
 * the backend soft-fails, and the caller treats any error as a no-op (the seeker
 * already has their reading). reading_id is the report's order_id.
 */
export async function rateReading(
  orderId: string,
  data: RateReadingRequest
): Promise<{ ok: boolean; public_review_received?: boolean }> {
  return apiRequest(`/reports/${encodeURIComponent(orderId)}/rate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}
